import { useCallback, useEffect, useRef } from 'react';
import { getVisitorId } from '../utils/abTest';
import { getDevice, isInternalVisit } from './useCommunityPageAnalytics';
import { isInAppBrowser, inAppName, iosVersion } from '../utils/inAppBrowser';

// Anonymous step tracking for a community's sign-up page: which of the form's
// steps each visit reached (typing, Google, the in-app browser sheet, errors,
// account created) so the admin Page Analytics tab can show where people give
// up. Best-effort — never blocks or throws on the page.

const HEARTBEAT_MS = 15 * 1000;
const MAX_STEPS = 40;
// Steps worth recording only the first time (focus / typing happen a lot).
const ONCE = new Set(['email_focus', 'email_typed', 'password_focus', 'password_typed', 'terms_ticked', 'google_blocked', 'sheet_steps']);

const newId = () => ((crypto.randomUUID && crypto.randomUUID()) || `sv_${Date.now()}_${Math.random().toString(36).slice(2)}`).replace(/[^A-Za-z0-9_-]/g, '');

const referrerHost = () => {
  try {
    if (!document.referrer) return null;
    const host = new URL(document.referrer).hostname.replace(/^www\./, '');
    return host === window.location.hostname.replace(/^www\./, '') ? '(in-site)' : host;
  } catch {
    return null;
  }
};

const useSignupAnalytics = (communityId) => {
  const ref = useRef(null);

  useEffect(() => {
    if (!communityId) return undefined;
    const apiBase = process.env.REACT_APP_API_BASE_URL;
    const s = {
      pageViewId: newId(),
      steps: [],
      accumulatedMs: 0,
      visibleSince: document.visibilityState === 'visible' ? Date.now() : null,
      exited: false,
    };
    const activeMs = () => s.accumulatedMs + (s.visibleSince ? Date.now() - s.visibleSince : 0);
    const base = {
      pageViewId: s.pageViewId,
      visitorId: getVisitorId(),
      communityId,
      device: getDevice(),
      inApp: isInAppBrowser() ? inAppName() : '',
      iosVersion: iosVersion() || undefined,
      referrer: referrerHost(),
    };
    const send = (extra = {}) => {
      try {
        fetch(`${apiBase}/analytics/signup-view`, {
          method: 'POST',
          keepalive: true,
          headers: { 'Content-Type': 'text/plain' },
          body: JSON.stringify({ ...base, internal: isInternalVisit(), activeMs: activeMs(), steps: s.steps, ...extra }),
        }).catch(() => {});
      } catch { /* ignore */ }
    };
    s.track = (step, detail) => {
      if (s.steps.length >= MAX_STEPS) return;
      if (ONCE.has(step) && s.steps.some(x => x.step === step)) return;
      s.steps.push({ step, ...(detail ? { detail: String(detail).slice(0, 120) } : {}), atMs: activeMs() });
      send();
    };
    ref.current = s;

    const first = setTimeout(() => send(), 1500);
    const beat = setInterval(() => { if (document.visibilityState === 'visible') send(); }, HEARTBEAT_MS);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        s.visibleSince = Date.now();
      } else {
        if (s.visibleSince) s.accumulatedMs += Date.now() - s.visibleSince;
        s.visibleSince = null;
        send({ exitType: 'left' });
      }
    };
    const onPageHide = () => { if (!s.exited) { s.exited = true; send({ exitType: 'left' }); } };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      clearTimeout(first);
      clearInterval(beat);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      if (!s.exited) { s.exited = true; send({ exitType: 'navigated' }); }
      ref.current = null;
    };
  }, [communityId]);

  // Stable function the form calls: track('email_typed'), track('submit_error', code)…
  return useCallback((step, detail) => { try { ref.current?.track(step, detail); } catch { /* ignore */ } }, []);
};

export default useSignupAnalytics;
